import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart2,
  Bot,
  Calendar,
  CheckCircle2,
  Clock,
  MessageCircle,
  Send,
  Share2,
  TrendingUp,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useTheme } from "next-themes";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminSocialSubNav } from "@/components/admin/AdminSocialSubNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

interface Stats {
  weeklyPosts: number;
  dmsResponded: number;
  dmTotal: number;
  dmAutoRate: number;
  appReferrals: number;
  engagementRate: number;
}

interface Post {
  id: string;
  platform: string;
  content: string;
  scheduled_at: string | null;
  status: string;
  post_type: string;
}

interface Connection {
  platform: string;
  handle: string | null;
  is_active: boolean;
}

const PLATFORM_ICONS: Record<string, string> = {
  instagram: "📸",
  tiktok: "🎵",
  facebook: "📘",
  twitter: "🐦",
  youtube: "▶️",
};

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  twitter: "Twitter",
  youtube: "YouTube",
};

const POST_TYPE_COLORS: Record<string, string> = {
  organic: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  ad: "bg-orange-50 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  educational: "bg-blue-50 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
  engagement: "bg-purple-50 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300",
};

const PLATFORM_DEFAULTS = ["instagram", "tiktok", "facebook"];

export default function AdminSocialDashboard() {
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const authenticated = !!sessionQuery.data?.authenticated;
  const configured = !!sessionQuery.data?.configured;
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const [stats, setStats] = useState<Stats | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [testInput, setTestInput] = useState("");
  const [testOutput, setTestOutput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authenticated) return;
    Promise.all([
      adminFetch("/api/admin/social/stats").then((r) => r.json()),
      adminFetch("/api/admin/social/posts?status=scheduled&limit=8").then((r) => r.json()),
      adminFetch("/api/admin/social/connections").then((r) => r.json()),
    ])
      .then(([s, p, c]) => {
        if (s.ok) setStats(s.data);
        if (p.ok) setPosts(p.data ?? []);
        if (c.ok) setConnections(c.data ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [authenticated]);

  async function handleTest() {
    if (!testInput.trim()) return;
    setGenerating(true);
    setTestOutput("");
    try {
      const r = await adminFetch("/api/admin/social/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "dm", context: testInput, platform: "instagram" }),
      });
      const json = await r.json();
      if (json.ok) setTestOutput(json.text);
    } catch {
      setTestOutput("Hata oluştu.");
    } finally {
      setGenerating(false);
    }
  }

  if (sessionQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  const connMap = Object.fromEntries(connections.map((c) => [c.platform, c]));

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                  Sosyal Medya
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <AdminSocialSubNav />

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            {loading ? (
              <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                      Sosyal Medya Paneli
                    </div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Luna AI ile içerik ve DM yönetimi
                    </div>
                  </div>
                  <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700">
                    <Link to="/admin/social/content-generator">
                      <Bot className="mr-2 h-4 w-4" />
                      İçerik Üret
                    </Link>
                  </Button>
                </div>

                {/* Stat kartları */}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {[
                    {
                      icon: <Send className="h-5 w-5" />,
                      label: "Bu Hafta Gönderi",
                      value: stats?.weeklyPosts ?? 0,
                      sub: "planlı & yayınlı",
                      color: "bg-blue-500/10 text-blue-600 dark:text-blue-300",
                    },
                    {
                      icon: <MessageCircle className="h-5 w-5" />,
                      label: "DM Yanıtlandı",
                      value: stats?.dmsResponded ?? 0,
                      sub: `%${stats?.dmAutoRate ?? 0} otomasyon`,
                      color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
                    },
                    {
                      icon: <TrendingUp className="h-5 w-5" />,
                      label: "Uygulama Yönlendirme",
                      value: stats?.appReferrals ?? 0,
                      sub: "bu hafta",
                      color: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
                    },
                    {
                      icon: <BarChart2 className="h-5 w-5" />,
                      label: "Etkileşim Oranı",
                      value: `%${stats?.engagementRate ?? 0}`,
                      sub: "ortalama",
                      color: "bg-orange-500/10 text-orange-600 dark:text-orange-300",
                    },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40"
                    >
                      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${s.color}`}>
                        {s.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          {s.label}
                        </div>
                        <div className="text-xl font-extrabold text-slate-900 dark:text-white">{s.value}</div>
                        <div className="text-xs text-slate-400 dark:text-slate-500">{s.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Platform bağlantıları */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Platform Bağlantıları
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {PLATFORM_DEFAULTS.map((platform) => {
                      const conn = connMap[platform];
                      const active = conn?.is_active;
                      return (
                        <div
                          key={platform}
                          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/50"
                        >
                          <span className="text-base">{PLATFORM_ICONS[platform]}</span>
                          <div>
                            <div className="text-xs font-medium text-slate-700 dark:text-slate-200">
                              {PLATFORM_LABELS[platform]}
                            </div>
                            <div className="text-[11px] text-slate-400 dark:text-slate-500">
                              {active ? (conn.handle ?? "Bağlı") : "Bağlı değil"}
                            </div>
                          </div>
                          {active ? (
                            <Wifi className="ml-1 h-3.5 w-3.5 text-emerald-500" />
                          ) : (
                            <WifiOff className="ml-1 h-3.5 w-3.5 text-slate-400" />
                          )}
                        </div>
                      );
                    })}
                    <Button asChild variant="outline" size="sm" className="rounded-full">
                      <Link to="/admin/social/connections">Yönet</Link>
                    </Button>
                  </div>
                </div>

                {/* İki kolon */}
                <div className="grid gap-4 lg:grid-cols-2">
                  {/* Takvim */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <Calendar className="mr-1.5 inline h-4 w-4" />
                        İçerik Takvimi
                      </h2>
                      <Button asChild variant="ghost" size="sm" className="h-7 text-xs text-slate-400">
                        <Link to="/admin/social/content-generator">+ Ekle</Link>
                      </Button>
                    </div>
                    <MiniCalendar posts={posts} />
                  </div>

                  {/* Sıradaki gönderiler */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <Clock className="mr-1.5 inline h-4 w-4" />
                        Sıradaki Gönderiler
                      </h2>
                    </div>
                    {posts.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">Planlanmış gönderi yok</p>
                    ) : (
                      <div className="space-y-2">
                        {posts.slice(0, 5).map((p) => (
                          <div
                            key={p.id}
                            className="flex items-start gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5 dark:bg-slate-900/40"
                          >
                            <span className="mt-0.5 text-sm">{PLATFORM_ICONS[p.platform] ?? "📄"}</span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs text-slate-700 dark:text-slate-200">
                                {p.content.slice(0, 60)}…
                              </p>
                              <p className="mt-0.5 text-[11px] text-muted-foreground">
                                {p.scheduled_at
                                  ? new Date(p.scheduled_at).toLocaleString("tr-TR", {
                                      day: "numeric",
                                      month: "short",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Taslak"}
                              </p>
                            </div>
                            <Badge
                              className={`shrink-0 text-[10px] ${POST_TYPE_COLORS[p.post_type] ?? "bg-slate-100 text-slate-500 dark:bg-slate-600/20 dark:text-slate-400"}`}
                            >
                              {p.post_type}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Luna DM test widget */}
                <div className="rounded-2xl border border-purple-200 bg-purple-50/50 p-4 dark:border-purple-700/30 dark:bg-purple-950/20">
                  <div className="mb-3 flex items-center gap-2">
                    <Bot className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                    <h2 className="text-sm font-medium text-purple-700 dark:text-purple-300">Luna DM Testi</h2>
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-7 text-xs text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300"
                    >
                      <Link to="/admin/social/luna-settings">Ayarlar</Link>
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Bir kullanıcı mesajı gir… (ör. 'Yaşam yolu sayım 7, ne önerirsin?')"
                      className="min-h-[64px] border-purple-200 bg-white text-sm dark:border-purple-700/40 dark:bg-slate-900 dark:text-slate-200"
                      value={testInput}
                      onChange={(e) => setTestInput(e.target.value)}
                    />
                    <div className="flex justify-end">
                      <Button
                        onClick={handleTest}
                        disabled={generating || !testInput.trim()}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {generating ? "Üretiliyor…" : "Luna'ya Sor"}
                      </Button>
                    </div>
                    {testOutput && (
                      <div className="rounded-lg border border-purple-200 bg-white p-3 text-sm whitespace-pre-wrap dark:border-purple-700/30 dark:bg-purple-900/20 dark:text-slate-200">
                        {testOutput}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniCalendar({ posts }: { posts: Post[] }) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const scheduledDays = new Set(
    posts.filter((p) => p.scheduled_at).map((p) => new Date(p.scheduled_at!).getDate()),
  );

  const cells: (number | null)[] = Array(firstDay === 0 ? 6 : firstDay - 1).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const DAY_LABELS = ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"];

  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] text-slate-400 dark:text-slate-500">
        {DAY_LABELS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => (
          <div
            key={i}
            className={`relative flex h-7 w-full items-center justify-center rounded text-[11px] ${
              day === null
                ? ""
                : day === today.getDate()
                  ? "bg-blue-600 font-bold text-white"
                  : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            }`}
          >
            {day}
            {day !== null && scheduledDays.has(day) && (
              <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-emerald-500" />
            )}
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-500" />
        Yeşil nokta = planlanmış gönderi
      </p>
    </div>
  );
}
